/**
 * Returns an iFrame src URL value for either YouTube or Vimeo hosted videos
 *
 * @param {string} host 'youtube' or 'vimeo'
 * @param {string} id A key/value object specifying which values to replace
 */
const externalVideoEmbedSrc = (host = 'youtube', id, autoplay = false) => {
  let src = ''
  switch (host) {
    case 'youtube':
      src = `https://www.youtube-nocookie.com/embed/${id}?${
        autoplay && `autoplay=1&amp;`
      }controls=0&amp;enablejsapi=1&amp;&loop=1&modestbranding=1&mute=1&cc_load_policy=0&disablekb=0&fs=1&iv_load_policy=1&playsinline=1&rel=1&widgetid=1`
      break
    case 'vimeo':
      src = `https://player.vimeo.com/video/${id}?${
        autoplay && `autoplay=1&amp;`
      }byline=0&amp;controls=1&amp;loop=1&amp;playsinline=1&amp;title=0`
      break
    default:
      console.error('Invalid host value (must be "youtube" or "vimeo")')
  }
  return src
}

class AdaptiveVideo extends HTMLElement {
  connectedCallback() {
    this.type = this.dataset.type
    this.video = JSON.parse(atob(this.dataset.video || 'e30='))
    this.url = this.dataset.url
    this.isSecondaryMedia = this.getPreviousSibling(this, 'adaptive-image')
    this.autoplay = this.dataset.autoplay === 'true'
    this.controls = this.dataset.controls === 'true'
    this.loop = this.dataset.loop === 'true'
    if (this.type !== 'video') {
      this.id = this.dataset.id || this.video?.external_id
    }
    this.host = this.dataset.host || this.video?.host
    this.alt = this.dataset.alt || this.video?.alt
    this.customControls = this.dataset.customControls === 'true'
    this.aspectRatio =
      (this.dataset.aspectRatio === 'wide' && 16 / 9) ||
      (this.dataset.aspectRatio === 'full' && 4 / 3) ||
      parseFloat(this.dataset.aspectRatio) ||
      this.video?.aspect_ratio ||
      16 / 9
    this.posterImage = this.dataset.posterImage
    this.objectFit = this.dataset.objectFit || 'contain'

    this.playAriaLabel = this.dataset.ariaPlay || 'Play video'
    this.pauseAriaLabel = this.dataset.ariaPause || 'Pause video'

    this.style.setProperty('--hh-object-fit', this.objectFit)
    this.generateControls = this.generateControls.bind(this)
    this._addControlListeners = this._addControlListeners.bind(this)
    this._playVideo = this._playVideo.bind(this)
    this._pauseVideo = this._pauseVideo.bind(this)

    this.attachShadow({ mode: 'open' })
    this.render()
    this._addControlListeners()

    if (this.isSecondaryMedia) {
      this.autoplayOnHover(this.shadowRoot)
    }
    if (this.autoplay) {
      this.playButton?.classList.add('hidden')
      this.handleIntersectionObserver()
    }
  }

  getPreviousSibling(elem, selector) {
    let sibling = elem.previousElementSibling

    if (!selector) return null

    while (sibling) {
      if (sibling.matches(selector)) {
        return sibling
      }
      sibling = sibling.previousElementSibling
    }
  }

  // For Product Cards, if the video is a secondary media following an image media type,
  // only autoplay the video on card hover to ensure that the video is not finished playing by
  // the time a user hovers over the card
  autoplayOnHover(shadowRoot) {
    const card = this.closest('.card-wrapper')
    if (!card) return
    card.addEventListener('mouseover', function () {
      shadowRoot.querySelector('video').play()
    })
    card.addEventListener('mouseout', function () {
      shadowRoot.querySelector('video').pause()
    })
  }

  generateControls() {
    let markup

    markup = `<div class="video-player-controls ${
      (this.controlsPosition && this.controlsPosition) || ''
    }">
      <ul>
        <li>
          <button class="play-video ${
            this.autoplay === false ? 'full-opacity' : ''
          }" aria-label="${this.playAriaLabel}">
            <svg
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
            focusable='false'
            class='icon icon-play'
            role='presentation'
            width='15'
            height='16'
            viewBox='0 0 15 16'
            fill='none'
          >
            <path d="M13.4922 8L1 15.1384L1 0.86159L13.4922 8Z" stroke="#FAF9F8"/>
          </svg>
          </button>
        </li>
        <li>
          <button class="pause-video ${
            this.autoplay === false ? 'hidden' : ''
          }" aria-label="${this.pauseAriaLabel}">
            <svg
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
            focusable='false'
            class='icon icon-pause'
            role='presentation'
            viewBox='0 0 16 16'
            width='16'
            height='16'
          >
            <line x1="4" x2="4" y1="0" y2="16" fill="none" stroke="#FAF9F8" stroke-miterlimit="10" stroke-width="2"/>
            <line x1="12" x2="12" y1="0" y2="16" fill="none" stroke="#FAF9F8" stroke-miterlimit="10" stroke-width="2"/>
          </svg>
          </button>
        </li>
      </ul>
    </div>`

    return markup
  }

  handleIntersectionObserver() {
    // adapted from https://benfrain.com/automatically-play-and-pause-video-as-it-enters-and-leaves-the-viewport-screen/
    const video = this.shadowRoot.querySelector('video')
    video.muted = true
    let playPromise = video.play()
    if (playPromise !== undefined) {
      playPromise.then((_) => {
        let observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // if more than 20% is visible AND it's paused, play
              if (entry.intersectionRatio >= 0.2 && video.paused) {
                video.play()
                // if it's less than 20% visible and it's playing, pause
              } else if (entry.intersectionRatio < 0.2) {
                video.pause()
              }
            })
          },
          { threshold: 0.2 }
        )
        observer.observe(video)
      })
    }
  }

  updateVideo(data) {
    Object.assign(this, data)
    this.render()
  }

  generateStyles() {
    return `
      <style>
        :host {
          display: block;
          aspect-ratio: ${this.aspectRatio};
          position: relative;
          width: 100%;
        }
        #video-element {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          object-fit: var(--hh-object-fit);
        }

        .video-player-controls {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 50;
        }

        .play-video, .pause-video {
          cursor: pointer;
          padding: 10px;
          border-radius: 60px;
          background: #2B2829;
          border: none;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .hidden {
          display: none !important;
        }

        .play-video svg, .pause-video svg {
          width: 16px;
          height: 16px;
          overflow: visible;
        }

        ul {
          padding: 0 !important;
          margin: 0 !important;
          list-style-type: none !important;
        }
      </style>
    `
  }

  generateVideoMarkup() {
    let sources = ''

    if (this.type === 'url') {
      sources = `<source src="${this.url}"></source>`
    } else if (this.type === 'video' && this.video.sources) {
      for (const source of this.video.sources) {
        sources += `<source src="${source.url}" type="${source.mime_type}"></source>`
      }
    }

    let markup = `
      <video
        id="video-element"
        playsinline="playsinline"
        ${this.controls ? `controls="controls"` : ''}
        ${this.dataset.autoplay === 'true' && `autoplay="autoplay"`}
        ${this.loop ? `loop="loop"` : ''}
        preload="none"
        muted="muted"

        aria-label="${this.alt}"
        ${(this.posterImage && `poster="${this.posterImage}"`) || ''}
      >
        ${sources}
        ${
          (this.posterImage &&
            `<img src="${this.posterImage}" alt="${this.alt}">`) ||
          ''
        }
      </video>
    `
    if (this.customControls) {
      markup += `${this.generateControls()}`
    }

    return markup
  }

  _playVideo() {
    if (this.customControls) {
      this.playButton?.classList.add('hidden')
      this.pauseButton.classList.remove('hidden')
    }

    return this.videoPlayer.play()
  }

  _pauseVideo() {
    if (this.customControls) {
      this.playButton?.classList.remove('hidden')
      this.pauseButton.classList.add('hidden')
    }
    return this.videoPlayer.pause()
  }

  _addControlListeners() {
    this.videoPlayer = this.shadowRoot.querySelector('video')

    this.playButton = this.shadowRoot.querySelector('.play-video')
    this.pauseButton = this.shadowRoot.querySelector('.pause-video')

    if (this.playButton)
      this.playButton?.addEventListener('click', this._playVideo)
    if (this.pauseButton)
      this.pauseButton.addEventListener('click', this._pauseVideo)
  }

  generateIframeMarkup() {
    return `
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${externalVideoEmbedSrc(this.host, this.id, this.autoplay)}"
        title="${this.alt}">
      </iframe>
    `
  }

  render() {
    this.shadowRoot.innerHTML = `
      ${this.generateStyles()}
      ${
        ['hosted_url', 'external_video'].includes(this.type)
          ? this.generateIframeMarkup()
          : this.generateVideoMarkup()
      }
    `
  }
}

window.customElements.define('adaptive-video', AdaptiveVideo)
